import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { PhysicsWorld } from '../physics/PhysicsWorld';

export interface Landmark {
  mesh: THREE.Group;
  bodies: CANNON.Body[];
  type: 'tower' | 'base' | 'arena' | 'monument';
  position: THREE.Vector3;
}

export class LandmarkPlacer {
  private scene: THREE.Scene;
  private physicsWorld: PhysicsWorld;
  private landmarks: Landmark[] = [];

  constructor(scene: THREE.Scene, physicsWorld: PhysicsWorld) {
    this.scene = scene;
    this.physicsWorld = physicsWorld;
  }

  public placeAll(): void {
    // Landmarks spread further for 1000x1000 map
    // Main tower in the far corner - the objective
    this.createMainTower(new THREE.Vector3(350, 0, 350));

    // Enemy base on opposite corner
    this.createEnemyBase(new THREE.Vector3(-350, 0, -350));

    // Battle arena in another corner
    this.createArena(new THREE.Vector3(350, 0, -350));

    // Monument/obelisk
    this.createMonument(new THREE.Vector3(-350, 0, 350));
  }

  private createMainTower(position: THREE.Vector3): Landmark {
    const group = new THREE.Group();
    const bodies: CANNON.Body[] = [];

    // Main spire - very tall central tower
    const spireHeight = 80;
    const spireGeom = new THREE.CylinderGeometry(3, 8, spireHeight, 8);
    const spireMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a3e,
      metalness: 0.9,
      roughness: 0.1,
    });
    const spire = new THREE.Mesh(spireGeom, spireMat);
    spire.position.y = spireHeight / 2;
    group.add(spire);

    // Glowing rings around spire
    for (let i = 0; i < 8; i++) {
      const ringGeom = new THREE.TorusGeometry(5 + i * 0.5, 0.3, 8, 32);
      const ringMat = new THREE.MeshStandardMaterial({
        color: 0x00ffff,
        emissive: 0x00ffff,
        emissiveIntensity: 2,
      });
      const ring = new THREE.Mesh(ringGeom, ringMat);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 10 + i * 9;
      group.add(ring);
    }

    // Base platform
    const baseGeom = new THREE.CylinderGeometry(15, 18, 3, 8);
    const baseMat = new THREE.MeshStandardMaterial({
      color: 0x2a2a4e,
      metalness: 0.8,
    });
    const base = new THREE.Mesh(baseGeom, baseMat);
    base.position.y = 1.5;
    group.add(base);

    // Neon edge on base
    const baseEdgeGeom = new THREE.TorusGeometry(16.5, 0.2, 8, 32);
    const baseEdgeMat = new THREE.MeshStandardMaterial({
      color: 0xff00ff,
      emissive: 0xff00ff,
      emissiveIntensity: 2,
    });
    const baseEdge = new THREE.Mesh(baseEdgeGeom, baseEdgeMat);
    baseEdge.rotation.x = Math.PI / 2;
    baseEdge.position.y = 3;
    group.add(baseEdge);

    // Beacon at top
    const beaconGeom = new THREE.SphereGeometry(2, 16, 16);
    const beaconMat = new THREE.MeshStandardMaterial({
      color: 0x00ffff,
      emissive: 0x00ffff,
      emissiveIntensity: 5,
    });
    const beacon = new THREE.Mesh(beaconGeom, beaconMat);
    beacon.position.y = spireHeight + 2;
    group.add(beacon);

    // Add light source
    const pointLight = new THREE.PointLight(0x00ffff, 2, 50);
    pointLight.position.y = spireHeight + 2;
    group.add(pointLight);

    group.position.copy(position);
    this.scene.add(group);

    // Physics body for base
    const baseBody = new CANNON.Body({
      mass: 0,
      shape: new CANNON.Cylinder(15, 18, 3, 8),
      position: new CANNON.Vec3(position.x, 1.5, position.z),
    });
    baseBody.collisionFilterGroup = 16;
    baseBody.collisionFilterMask = 2 | 4;
    this.physicsWorld.addBody(baseBody);
    bodies.push(baseBody);

    // Physics body for spire
    const spireBody = new CANNON.Body({
      mass: 0,
      shape: new CANNON.Cylinder(3, 8, spireHeight, 8),
      position: new CANNON.Vec3(position.x, spireHeight / 2, position.z),
    });
    spireBody.collisionFilterGroup = 16;
    spireBody.collisionFilterMask = 2 | 4;
    this.physicsWorld.addBody(spireBody);
    bodies.push(spireBody);

    const landmark: Landmark = {
      mesh: group,
      bodies,
      type: 'tower',
      position,
    };
    this.landmarks.push(landmark);
    return landmark;
  }

  private createEnemyBase(position: THREE.Vector3): Landmark {
    const group = new THREE.Group();
    const bodies: CANNON.Body[] = [];

    // Hexagonal fortress walls
    const wallHeight = 12;
    const wallRadius = 25;
    const wallThickness = 2;

    for (let i = 0; i < 6; i++) {
      const angle = (i / 6) * Math.PI * 2;
      const nextAngle = ((i + 1) / 6) * Math.PI * 2;

      const x1 = Math.cos(angle) * wallRadius;
      const z1 = Math.sin(angle) * wallRadius;
      const x2 = Math.cos(nextAngle) * wallRadius;
      const z2 = Math.sin(nextAngle) * wallRadius;

      const wallLength = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(z2 - z1, 2));
      const wallGeom = new THREE.BoxGeometry(wallLength, wallHeight, wallThickness);
      const wallMat = new THREE.MeshStandardMaterial({
        color: 0x2a1a1a,
        metalness: 0.7,
        roughness: 0.3,
      });
      const wall = new THREE.Mesh(wallGeom, wallMat);

      wall.position.set((x1 + x2) / 2, wallHeight / 2, (z1 + z2) / 2);
      wall.rotation.y = -angle - Math.PI / 6;
      group.add(wall);

      // Top trim
      const trimGeom = new THREE.BoxGeometry(wallLength, 0.5, wallThickness + 0.2);
      const trimMat = new THREE.MeshStandardMaterial({
        color: 0xff0000,
        emissive: 0xff0000,
        emissiveIntensity: 1.5,
      });
      const trim = new THREE.Mesh(trimGeom, trimMat);
      trim.position.set((x1 + x2) / 2, wallHeight + 0.25, (z1 + z2) / 2);
      trim.rotation.y = -angle - Math.PI / 6;
      group.add(trim);

      // Physics for wall
      const wallBody = new CANNON.Body({
        mass: 0,
        shape: new CANNON.Box(new CANNON.Vec3(wallLength / 2, wallHeight / 2, wallThickness / 2)),
        position: new CANNON.Vec3(
          position.x + (x1 + x2) / 2,
          wallHeight / 2,
          position.z + (z1 + z2) / 2
        ),
      });
      wallBody.quaternion.setFromAxisAngle(new CANNON.Vec3(0, 1, 0), -angle - Math.PI / 6);
      wallBody.collisionFilterGroup = 16;
      wallBody.collisionFilterMask = 2 | 4;
      this.physicsWorld.addBody(wallBody);
      bodies.push(wallBody);
    }

    // Central command building
    const cmdGeom = new THREE.BoxGeometry(15, 20, 15);
    const cmdMat = new THREE.MeshStandardMaterial({
      color: 0x1a0a0a,
      metalness: 0.8,
    });
    const cmdBuilding = new THREE.Mesh(cmdGeom, cmdMat);
    cmdBuilding.position.y = 10;
    group.add(cmdBuilding);

    // Red glow on command building
    const glowGeom = new THREE.BoxGeometry(15.2, 0.5, 15.2);
    const glowMat = new THREE.MeshStandardMaterial({
      color: 0xff0000,
      emissive: 0xff0000,
      emissiveIntensity: 2,
    });
    for (let i = 0; i < 4; i++) {
      const glow = new THREE.Mesh(glowGeom, glowMat);
      glow.position.y = 5 + i * 5;
      group.add(glow);
    }

    // Radar dish on top
    const dishGeom = new THREE.ConeGeometry(4, 2, 16, 1, true);
    const dishMat = new THREE.MeshStandardMaterial({
      color: 0x333333,
      metalness: 0.9,
      side: THREE.DoubleSide,
    });
    const dish = new THREE.Mesh(dishGeom, dishMat);
    dish.rotation.x = Math.PI;
    dish.position.y = 22;
    group.add(dish);

    group.position.copy(position);
    this.scene.add(group);

    // Central building physics
    const cmdBody = new CANNON.Body({
      mass: 0,
      shape: new CANNON.Box(new CANNON.Vec3(7.5, 10, 7.5)),
      position: new CANNON.Vec3(position.x, 10, position.z),
    });
    cmdBody.collisionFilterGroup = 16;
    cmdBody.collisionFilterMask = 2 | 4;
    this.physicsWorld.addBody(cmdBody);
    bodies.push(cmdBody);

    const landmark: Landmark = {
      mesh: group,
      bodies,
      type: 'base',
      position,
    };
    this.landmarks.push(landmark);
    return landmark;
  }

  private createArena(position: THREE.Vector3): Landmark {
    const group = new THREE.Group();
    const bodies: CANNON.Body[] = [];

    // Circular arena walls
    const arenaRadius = 30;
    const wallHeight = 8;
    const segments = 24;

    for (let i = 0; i < segments; i++) {
      // Leave gaps for entrances
      if (i % 6 === 0) continue;

      const angle = (i / segments) * Math.PI * 2;
      const wallGeom = new THREE.BoxGeometry(8, wallHeight, 2);
      const wallMat = new THREE.MeshStandardMaterial({
        color: 0x2a2a4e,
        metalness: 0.6,
      });
      const wall = new THREE.Mesh(wallGeom, wallMat);
      wall.position.set(
        Math.cos(angle) * arenaRadius,
        wallHeight / 2,
        Math.sin(angle) * arenaRadius
      );
      wall.rotation.y = -angle + Math.PI / 2;
      group.add(wall);

      // Neon top strip
      const stripGeom = new THREE.BoxGeometry(8.2, 0.3, 2.2);
      const stripMat = new THREE.MeshStandardMaterial({
        color: 0x8000ff,
        emissive: 0x8000ff,
        emissiveIntensity: 1.5,
      });
      const strip = new THREE.Mesh(stripGeom, stripMat);
      strip.position.set(
        Math.cos(angle) * arenaRadius,
        wallHeight + 0.15,
        Math.sin(angle) * arenaRadius
      );
      strip.rotation.y = -angle + Math.PI / 2;
      group.add(strip);
    }

    // Arena floor with pattern
    const floorGeom = new THREE.CircleGeometry(arenaRadius - 2, 32);
    const floorMat = new THREE.MeshStandardMaterial({
      color: 0x0a0a15,
      metalness: 0.5,
    });
    const floor = new THREE.Mesh(floorGeom, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = 0.05;
    group.add(floor);

    // Concentric rings on floor
    for (let r = 5; r < arenaRadius; r += 8) {
      const ringGeom = new THREE.TorusGeometry(r, 0.1, 8, 64);
      const ringMat = new THREE.MeshStandardMaterial({
        color: 0x8000ff,
        emissive: 0x8000ff,
        emissiveIntensity: 0.5,
      });
      const ring = new THREE.Mesh(ringGeom, ringMat);
      ring.rotation.x = Math.PI / 2;
      ring.position.y = 0.06;
      group.add(ring);
    }

    group.position.copy(position);
    this.scene.add(group);

    const landmark: Landmark = {
      mesh: group,
      bodies,
      type: 'arena',
      position,
    };
    this.landmarks.push(landmark);
    return landmark;
  }

  private createMonument(position: THREE.Vector3): Landmark {
    const group = new THREE.Group();
    const bodies: CANNON.Body[] = [];

    // Obelisk
    const obeliskHeight = 40;
    const obeliskGeom = new THREE.ConeGeometry(3, obeliskHeight, 4);
    const obeliskMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a2e,
      metalness: 0.95,
      roughness: 0.05,
    });
    const obelisk = new THREE.Mesh(obeliskGeom, obeliskMat);
    obelisk.position.y = obeliskHeight / 2;
    group.add(obelisk);

    // Glowing edges
    const edgesGeom = new THREE.EdgesGeometry(obeliskGeom);
    const edgesMat = new THREE.LineBasicMaterial({
      color: 0x00ffff,
    });
    const edges = new THREE.LineSegments(edgesGeom, edgesMat);
    edges.position.y = obeliskHeight / 2;
    group.add(edges);

    // Base platform
    const baseGeom = new THREE.BoxGeometry(12, 2, 12);
    const baseMat = new THREE.MeshStandardMaterial({
      color: 0x2a2a4e,
      metalness: 0.7,
    });
    const base = new THREE.Mesh(baseGeom, baseMat);
    base.position.y = 1;
    group.add(base);

    // Corner pillars with lights
    const pillarPositions = [
      new THREE.Vector3(-6, 0, -6),
      new THREE.Vector3(6, 0, -6),
      new THREE.Vector3(-6, 0, 6),
      new THREE.Vector3(6, 0, 6),
    ];

    for (const pillarPos of pillarPositions) {
      const pillarGeom = new THREE.CylinderGeometry(0.5, 0.5, 6, 8);
      const pillarMat = new THREE.MeshStandardMaterial({
        color: 0x333333,
        metalness: 0.8,
      });
      const pillar = new THREE.Mesh(pillarGeom, pillarMat);
      pillar.position.copy(pillarPos);
      pillar.position.y = 3;
      group.add(pillar);

      // Light on top
      const lightGeom = new THREE.SphereGeometry(0.5, 8, 8);
      const lightMat = new THREE.MeshStandardMaterial({
        color: 0xff00ff,
        emissive: 0xff00ff,
        emissiveIntensity: 2,
      });
      const light = new THREE.Mesh(lightGeom, lightMat);
      light.position.copy(pillarPos);
      light.position.y = 6.5;
      group.add(light);
    }

    group.position.copy(position);
    this.scene.add(group);

    // Physics body
    const obeliskBody = new CANNON.Body({
      mass: 0,
      shape: new CANNON.Cylinder(0.1, 3, obeliskHeight, 4),
      position: new CANNON.Vec3(position.x, obeliskHeight / 2, position.z),
    });
    obeliskBody.collisionFilterGroup = 16;
    obeliskBody.collisionFilterMask = 2 | 4;
    this.physicsWorld.addBody(obeliskBody);
    bodies.push(obeliskBody);

    const baseBody = new CANNON.Body({
      mass: 0,
      shape: new CANNON.Box(new CANNON.Vec3(6, 1, 6)),
      position: new CANNON.Vec3(position.x, 1, position.z),
    });
    baseBody.collisionFilterGroup = 16;
    baseBody.collisionFilterMask = 2 | 4;
    this.physicsWorld.addBody(baseBody);
    bodies.push(baseBody);

    const landmark: Landmark = {
      mesh: group,
      bodies,
      type: 'monument',
      position,
    };
    this.landmarks.push(landmark);
    return landmark;
  }

  public getLandmarks(): Landmark[] {
    return this.landmarks;
  }

  public getLandmarkByType(type: Landmark['type']): Landmark | undefined {
    return this.landmarks.find((l) => l.type === type);
  }

  public destroy(): void {
    for (const landmark of this.landmarks) {
      this.scene.remove(landmark.mesh);
      for (const body of landmark.bodies) {
        this.physicsWorld.removeBody(body);
      }
    }
    this.landmarks = [];
  }
}
